import {defineMessages, FormattedMessage} from 'react-intl';
import {Text, Overlay, Link} from 'src/v2/components/common';
import {WalletIcon} from 'src/v2/components/wallet/WalletIcon';
import {useSelector, setValue} from 'src/v2/state/store';
import type {WalletOption} from 'src/v2/state/types';
import {safeWindowOpen} from '@stripe-internal/safe-links';

const messages = defineMessages({
  continueTransaction: {
    id: 'crypto.payins.v2.wallet_connection_pending.continue_transaction',
    description: 'Prompt to tell the user to connect their wallet',
    defaultMessage: 'Confirm connection to {walletProvider}',
  },
  iosPrompt: {
    id: 'crypto.payins.v2.wallet_connection_pending.ios_prompt',
    description: 'Prompt to tell the user to install the wallet app on iOS',
    defaultMessage:
      'If the app doesn’t open, please download the {walletProvider} app from <Link>App Store</Link>.',
  },
  androidPrompt: {
    id: 'crypto.payins.v2.wallet_connection_pending.android_prompt',
    description: 'Prompt to tell the user to install the wallet app on Android',
    defaultMessage:
      'If the app doesn’t open, please download the {walletProvider} app from <Link>Google Play</Link>.',
  },
  cancel: {
    id: 'crypto.payins.v2.wallet_connection_pending.cancel',
    description: 'Prompt to tell the user to cancel the connection',
    defaultMessage: 'Cancel',
  },
});

const MobileWalletConnectPrompt = ({
  walletOption,
}: {
  walletOption: WalletOption;
}) => {
  const device = useSelector((state) => state.walletConnection.device);

  if (device === 'desktop') {
    return null;
  }

  const {downloadLinks, name} = walletOption;

  const deviceConfig = {
    ios: {
      message: messages.iosPrompt,
      url: `https://apps.apple.com/app/${downloadLinks.iosId}`,
    },
    android: {
      message: messages.androidPrompt,
      url: `https://play.google.com/store/apps/details?id=${downloadLinks.androidId}`,
    },
  };

  const currentDeviceConfig = deviceConfig[device];

  return (
    <>
      <Text type="body" css={{textColor: 'inherit'}}>
        {currentDeviceConfig ? (
          <FormattedMessage
            {...currentDeviceConfig.message}
            values={{
              walletProvider: name,
              Link: (msg) => (
                <Link
                  type="secondary"
                  onClick={() => {
                    safeWindowOpen(currentDeviceConfig.url);
                  }}
                >
                  {msg}
                </Link>
              ),
            }}
          />
        ) : null}
      </Text>
      <Link
        type="secondary"
        onPress={() => {
          setValue((state) => {
            state.overlay = null;
          });
        }}
      >
        <FormattedMessage {...messages.cancel} />
      </Link>
    </>
  );
};

export const WalletConnectionPendingOverlay = () => {
  const newWallet = useSelector((state) => state.newWallet);

  if (!newWallet) {
    throw new Error('newWallet is required in WalletConnectionPendingOverlay');
  }

  return (
    <Overlay name="wallet-connection-pending">
      <WalletIcon walletOption={newWallet.walletOption} />
      <Text type="title" css={{textColor: 'inherit'}}>
        <FormattedMessage
          {...messages.continueTransaction}
          values={{walletProvider: newWallet.walletOption.name}}
        />
      </Text>
      <MobileWalletConnectPrompt walletOption={newWallet.walletOption} />
    </Overlay>
  );
};
